/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import clsx from 'clsx';
import React, { MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { CHART_COLORS } from '@futrli/colors';
import { FormattedMessage } from '@futrli/localisation';
import { UIFeatherIcon, UILoader } from '@futrli/ui-components';

import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { LayoutItem as Layout } from '../grid-layout';
import { CashInOut } from '../../../forecast/detail/performance/cash-flow-performance/cash-in-out';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { TopOwed } from '../../../forecast/detail/performance/cash-flow-performance/top-owed';
import { WidgetCard } from '../widget-card/widget-card';
import { useReportBuilderContext } from '../../report-builder.context';
import { APP_WIDGET_NAMES, CARD_TYPES, LayoutData, ONBOARDING_STEPS } from '../../types';
import { TextCard } from '../text-card/text-card';
import { TopCashTable } from '../../../forecast/detail/performance/cash-flow-performance/top-cash-table';
import { PERIODS } from '../../../forecast/detail/performance/components/period-dropdown';
import { ImageCard } from '../image-card/image-card';
import { useOnboardingContext } from '../../onboarding.context';
import { TopDebtBalances } from '../../../forecast/detail/performance/balance-sheet-performance/top-debt-balances';
import { TopRatios } from '../../../forecast/detail/performance/balance-sheet-performance/top-ratios';
import { CustomersAndSuppliersWidget } from '../../../forecast/detail/performance/customers-and-suppliers/customers-and-suppliers-widget';
import { LAYOUT_STEP } from '../../constants';
import { inchesToPX } from '../../utils-layout';
import { useI18nConstants } from '../../use-i18n-constants';
import { TableFiltersWithSort } from '../../../utils/table-filters/filter-table-types';

import styles from './page-card.scss';

interface Props {
  onDelete?: () => void;
  onDuplicate?: () => void;
  cardData: LayoutData;
  layoutInfo: Layout;
  viewOnly?: boolean;
  currency?: string;
}

export const DEFAULT_APP_WIDGET_ZOOM = 0.8;

export const PageCard = ({ onDelete, onDuplicate, cardData, layoutInfo, viewOnly, currency: propsCurrency }: Props) => {
  const intl = useIntl();
  const { currency: contextCurrency } = useContext(PredictManagementContext);
  const currency = contextCurrency || propsCurrency || '';
  const { dummyTemplateForecasts, hasTemplateDS } = useI18nConstants();
  const {
    cashflowMetrics,
    setCurrentCardId,
    currentCardId,
    profitAndLossMetrics,
    pageSetup: { margins, orientation },
    isTemplate,
    isWorkspaceTemplate,
    fetchCashflowMetrics,
    fetchProfitAndLossMetrics,
    setAppWidgetSelectedPeriod,
    setWidgetFiltersAndSort,
    setDataRefsToMap,
    setDatasetsToMap,
    setMapTemplateDataModalOpen,
    setMapDatasetsModalOpen,
    isOnboarding,
    reportDsToMap,
    reportFieldsToMap,
    balanceSheetMetrics,
    fetchBalanceSheetMetrics,
    pageLayoutData,
    isEditingTitle,
    getPageHeight,
    headerFooterLimits
  } = useReportBuilderContext();
  const [needsDSMapping, setNeedsDSMapping] = useState(false);
  const [needsMapping, setNeedsMapping] = useState(false);
  const [tooltipText, setTooltipText] = useState('');

  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;

  const { currentStep, setIsNextEnabled } = useOnboardingContext();

  useEffect(() => {
    if (
      !isFutrliTemplate &&
      cardData.type === CARD_TYPES.WIDGET &&
      hasTemplateDS(cardData[CARD_TYPES.WIDGET]?.selectedDatasets)
    ) {
      setNeedsDSMapping(true);
    } else {
      setNeedsDSMapping(false);
    }
  }, [cardData, isFutrliTemplate, hasTemplateDS]);

  const dsToMap = useMemo(
    () => reportDsToMap.filter(d => (cardData[CARD_TYPES.WIDGET]?.selectedDatasets || []).includes(d?.id)),
    [cardData, reportDsToMap]
  );

  const fieldsToMap = useMemo(
    () => reportFieldsToMap.filter(e => cardData[CARD_TYPES.WIDGET]?.selectedFinFields.find(f => f.ref === e.ref)),
    [cardData, reportFieldsToMap]
  );

  useEffect(() => {
    setNeedsMapping(!!(dsToMap.length || fieldsToMap.length));
    let text = '';
    if (dsToMap.length && !fieldsToMap.length) {
      text = intl.formatMessage({
        id: 'reporting.map_datasets_or_remove',
        defaultMessage: 'Map Datasets, or remove from Selected Datasets'
      });
    }
    if (!dsToMap.length && fieldsToMap.length) {
      text =
        fieldsToMap.length > 1
          ? intl.formatMessage({
              id: 'reporting.map_data_or_delete',
              defaultMessage: 'Map Data, or delete'
            })
          : intl.formatMessage(
              {
                id: 'reporting.map_by_type_or_delete',
                defaultMessage: 'Map {type}, or delete'
              },
              {
                type:
                  fieldsToMap[0].type === 'assumption'
                    ? intl.formatMessage({
                        id: 'common.prediction',
                        defaultMessage: 'Prediction'
                      })
                    : fieldsToMap[0].type === 'row_formula'
                    ? fieldsToMap[0].name
                    : fieldsToMap[0].type
              }
            );
    }
    setTooltipText(
      text ||
        intl.formatMessage({
          id: 'reporting.map_datasets_and_data_or_delete',
          defaultMessage: 'Map Datasets and Data, or delete'
        })
    );
  }, [dsToMap, fieldsToMap, intl]);

  // Fetch metrics for app widgets - kept separate to avoid setState in render warning
  useEffect(() => {
    const widgetName = cardData[CARD_TYPES.APP_WIDGET]?.name;

    if (
      widgetName &&
      !cashflowMetrics &&
      [
        APP_WIDGET_NAMES.CASH_IN_FROM_INVOICES,
        APP_WIDGET_NAMES.CASH_OUT_FROM_BILLS,
        APP_WIDGET_NAMES.TOP_INVOICES_DUE,
        APP_WIDGET_NAMES.TOP_BILLS_OWED,
        APP_WIDGET_NAMES.ACCOUNT_BRINGING_MOST_CASH,
        APP_WIDGET_NAMES.CUSTOMERS_PAID_MOST,
        APP_WIDGET_NAMES.ACCOUNTS_GREATEST_CASH_EXPENDITURE,
        APP_WIDGET_NAMES.SUPPLIERS_YOU_PAID_MOST
      ].includes(widgetName)
    ) {
      fetchCashflowMetrics();
    }

    if (
      widgetName &&
      !profitAndLossMetrics &&
      [APP_WIDGET_NAMES.TOP_SALES_ACCOUNTS, APP_WIDGET_NAMES.TOP_COST_AND_EXPENSES_ACCOUNTS].includes(widgetName)
    ) {
      fetchProfitAndLossMetrics();
    }

    if (widgetName && !balanceSheetMetrics && [APP_WIDGET_NAMES.TOP_DEBT_BALANCES, APP_WIDGET_NAMES.RATIOS].includes(widgetName)) {
      fetchBalanceSheetMetrics();
    }
  }, [
    cardData,
    cashflowMetrics,
    profitAndLossMetrics,
    balanceSheetMetrics,
    fetchCashflowMetrics,
    fetchProfitAndLossMetrics,
    fetchBalanceSheetMetrics
  ]);

  const onPeriodChange = useCallback(
    (p: PERIODS) => {
      if (p && !viewOnly && currentCardId) {
        setAppWidgetSelectedPeriod(p);
      }
    },
    [viewOnly, currentCardId, setAppWidgetSelectedPeriod]
  );

  const onFiltersSortChange = useCallback(
    (filtersAndSort: TableFiltersWithSort) => {
      if (!viewOnly && currentCardId) {
        setWidgetFiltersAndSort(filtersAndSort);
      }
    },
    [currentCardId, setWidgetFiltersAndSort, viewOnly]
  );

  const handleCardClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isOnboarding) {
      const isSpecificOnboardingWidget = cardData.i === pageLayoutData[0].i;
      if (isSpecificOnboardingWidget && currentStep === ONBOARDING_STEPS.SELECT_WIDGET) {
        setIsNextEnabled(true);
        setCurrentCardId(cardData.i);
      }
    } else {
      if (!viewOnly && !isEditingTitle) {
        setCurrentCardId(cardData.i);
      }
    }
  };

  const handleCardMouseDown = () => {
    if (!viewOnly && !isOnboarding && !isEditingTitle) {
      setCurrentCardId(cardData.i);
    }
  };

  const handleOnDelete = (e: MouseEvent) => {
    e.stopPropagation();
    onDelete && onDelete();
  };

  const handleOnDuplicate = (e: MouseEvent) => {
    e.stopPropagation();
    onDuplicate && onDuplicate();
  };

  const handleOnTriggerMapping = () => {
    setDatasetsToMap(dsToMap.map(ds => ds.id));
    setDataRefsToMap(fieldsToMap.map(f => f.ref) as string[]);
    setMapTemplateDataModalOpen(true);
  };

  const handleOnTriggerDSMapping = () => {
    const templateDsToMap = dummyTemplateForecasts
      .filter(tds => (cardData[CARD_TYPES.WIDGET]?.selectedDatasets || []).includes(tds?.id))
      .map(ds => ds.id);
    setDatasetsToMap(templateDsToMap);
    setMapDatasetsModalOpen(true);
  };

  const renderAppWidget = (
    name: APP_WIDGET_NAMES | undefined,
    period: PERIODS = PERIODS.THIS_MONTH,
    defaultFiltersAndSort?: TableFiltersWithSort
  ) => {
    let nextLimit = 0;
    headerFooterLimits.current.some(l => {
      nextLimit = l[0];
      return l[0] > layoutInfo.y;
    });
    // since page limit (where you can place widgets) does not necessarily align with the natural end of page
    // we calculate the remaining left over to pass for initial space
    const leftOver =
      Math.round((nextLimit * LAYOUT_STEP) / getPageHeight()) * getPageHeight() -
      inchesToPX(margins[0].value) -
      inchesToPX(margins[2].value) -
      nextLimit * LAYOUT_STEP;
    const zoomAdjustment = 1 / DEFAULT_APP_WIDGET_ZOOM;
    const breakSpace =
      Math.ceil((inchesToPX(margins[0].value) + inchesToPX(margins[2].value)) / LAYOUT_STEP) * LAYOUT_STEP;
    switch (name) {
      case APP_WIDGET_NAMES.CASH_IN_FROM_INVOICES:
        return cashflowMetrics ? (
          <CashInOut data={cashflowMetrics.cash_in_from_invoices} type="in" currency={currency} />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.CASH_OUT_FROM_BILLS:
        return cashflowMetrics ? (
          <CashInOut data={cashflowMetrics.cash_out_from_bills} type="out" currency={currency} />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.TOP_INVOICES_DUE:
        return cashflowMetrics ? (
          <TopOwed data={cashflowMetrics.top_invoices_owed || []} type="invoices" currency={currency} />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.TOP_BILLS_OWED:
        return cashflowMetrics ? (
          <TopOwed data={cashflowMetrics?.top_bills_owed || []} type="bills" currency={currency} />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.TOP_SALES_ACCOUNTS:
        return profitAndLossMetrics ? (
          <TopCashTable
            data={profitAndLossMetrics?.top_sales_accounts}
            currency={currency}
            noDataMessage={
              <FormattedMessage
                id="reporting.no_top_sales_accounts"
                defaultMessage="No top sales accounts available for this time period."
              />
            }
            cardTitle={<FormattedMessage id="reporting.top_sales_accounts" defaultMessage="Top sales accounts" />}
            testId="top-sales-account-table"
            customHeaders={[
              <FormattedMessage key="common.name" id="common.name" defaultMessage="Name" />,
              <FormattedMessage key="common.amount" id="common.amount" defaultMessage="Amount" />,
              <FormattedMessage
                key="common.percent_revenue"
                id="common.percent_revenue"
                defaultMessage="% of revenue"
              />
            ]}
            customKeys={['name', 'amount', 'percent_of_revenue']}
            customColors={CHART_COLORS.POSITIVE.SECONDARY}
            defaultPeriod={period}
            onPeriodChanged={onPeriodChange}
          />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.TOP_COST_AND_EXPENSES_ACCOUNTS:
        return profitAndLossMetrics ? (
          <TopCashTable
            data={profitAndLossMetrics?.top_expense_accounts}
            currency={currency}
            noDataMessage={
              <FormattedMessage
                id="reporting.no_top_cost_expense_accounts"
                defaultMessage="No top cost & expense accounts available for this time period."
              />
            }
            cardTitle={
              <FormattedMessage id="reporting.top_cost_expense_accounts" defaultMessage="Top cost & expense accounts" />
            }
            testId="top-cost-and-expense-accounts-table"
            customHeaders={[
              <FormattedMessage key="common.name" id="common.name" defaultMessage="Name" />,
              <FormattedMessage key="common.amount" id="common.amount" defaultMessage="Amount" />,
              <FormattedMessage
                key="common.percent_revenue"
                id="common.percent_revenue"
                defaultMessage="% of revenue"
              />
            ]}
            customKeys={['name', 'amount', 'percent_of_revenue']}
            customColors={CHART_COLORS.NEGATIVE.SECONDARY}
            defaultPeriod={period}
            onPeriodChanged={onPeriodChange}
          />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.ACCOUNT_BRINGING_MOST_CASH:
        return cashflowMetrics ? (
          <TopCashTable
            data={cashflowMetrics?.top_cash_in_accounts}
            type="in"
            currency={currency}
            noDataMessage={
              <FormattedMessage
                id="reporting.no_payments_in"
                defaultMessage="No customer invoices have been paid in this time period."
              />
            }
            cardTitle={
              <FormattedMessage id="reporting.accounts_cash_in" defaultMessage="Accounts bringing in the most cash" />
            }
            defaultPeriod={period}
            onPeriodChanged={onPeriodChange}
          />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.CUSTOMERS_PAID_MOST:
        return cashflowMetrics ? (
          <TopCashTable
            data={cashflowMetrics?.top_customers}
            type="in"
            currency={currency}
            noDataMessage={
              <FormattedMessage
                id="reporting.no_payments_in"
                defaultMessage="No customer invoices have been paid in this time period."
              />
            }
            cardTitle={
              <FormattedMessage id="reporting.customers_paid_most" defaultMessage="Customers who have paid the most" />
            }
            defaultPeriod={period}
            onPeriodChanged={onPeriodChange}
          />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.ACCOUNTS_GREATEST_CASH_EXPENDITURE:
        return cashflowMetrics ? (
          <TopCashTable
            data={cashflowMetrics?.top_cash_out_accounts}
            type="out"
            currency={currency}
            noDataMessage={
              <FormattedMessage
                id="reporting.no_cash_out"
                defaultMessage="No supplier invoices have been paid in this time period."
              />
            }
            cardTitle={
              <FormattedMessage
                id="reporting.accounts_cash_out"
                defaultMessage="Accounts with the greatest cash expenditure"
              />
            }
            defaultPeriod={period}
            onPeriodChanged={onPeriodChange}
          />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.SUPPLIERS_YOU_PAID_MOST:
        return cashflowMetrics ? (
          <TopCashTable
            data={cashflowMetrics?.top_suppliers}
            type="out"
            currency={currency}
            noDataMessage={
              <FormattedMessage
                id="reporting.no_cash_out"
                defaultMessage="No supplier invoices have been paid in this time period."
              />
            }
            cardTitle={
              <FormattedMessage id="reporting.suppliers_paid_most" defaultMessage="Suppliers you have paid the most" />
            }
            defaultPeriod={period}
            onPeriodChanged={onPeriodChange}
          />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.TOP_DEBT_BALANCES:
        return balanceSheetMetrics ? (
          <TopDebtBalances data={balanceSheetMetrics.top_debt_accounts} />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.RATIOS:
        return balanceSheetMetrics ? (
          <TopRatios ratios={balanceSheetMetrics.ratios} />
        ) : (
          <div className={styles.pageCardLoaderContainer}>
            <UILoader />
          </div>
        );
      case APP_WIDGET_NAMES.CUSTOMERS_AND_SUPPLIERS:
        return (
          <div style={{ padding: 5, height: '100%', overflowY: currentCardId === cardData.i ? 'auto' : 'hidden' }}>
            <CustomersAndSuppliersWidget
              breakWidget={orientation !== 'dashboard'}
              initialSpace={(nextLimit - layoutInfo.y) * LAYOUT_STEP + leftOver}
              maxSpace={getPageHeight() - breakSpace}
              breakSpace={breakSpace}
              zoomAdjustment={zoomAdjustment}
              onPeriodChanged={onPeriodChange}
              defaultPeriod={period}
              onFiltersSortChange={onFiltersSortChange}
              defaultFiltersAndSort={defaultFiltersAndSort}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderCard = () => {
    switch (cardData.type) {
      case CARD_TYPES.TEXT:
        return <TextCard html={cardData[CARD_TYPES.TEXT]?.html || ''} cardId={cardData.i} />;
      case CARD_TYPES.APP_WIDGET:
        return (
          <div
            className={clsx(styles.appWidget)}
            style={{
              transform: `scale(${DEFAULT_APP_WIDGET_ZOOM})`,
              transformOrigin: 'top left',
              width: `${100 / DEFAULT_APP_WIDGET_ZOOM}%`,
              height: `${100 / DEFAULT_APP_WIDGET_ZOOM}%`
            }}
          >
            {renderAppWidget(
              cardData[CARD_TYPES.APP_WIDGET]?.name,
              cardData[CARD_TYPES.APP_WIDGET]?.period,
              cardData[CARD_TYPES.APP_WIDGET]?.widgetFilters
            )}
          </div>
        );
      case CARD_TYPES.WIDGET:
        return <WidgetCard cardData={cardData} layoutInfo={layoutInfo} viewOnly={viewOnly} currency={currency} />;
      case CARD_TYPES.IMAGE:
        return <ImageCard cardData={cardData} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={clsx(
        styles.pageCard,
        !viewOnly && styles.isDraggable,
        !viewOnly && currentCardId === cardData.i && styles.selectedCard,
        isTemplate && styles.isTemplate,
        !isFutrliTemplate && (needsDSMapping || needsMapping) && styles.needsMapping,
        cardData.type === CARD_TYPES.APP_WIDGET && styles.alwaysHideOverflow
      )}
      onClick={handleCardClick}
      onMouseDown={handleCardMouseDown}
    >
      {renderCard()}
      {!isOnboarding && (
        <div className={styles.widgetActions}>
          {!isFutrliTemplate && needsDSMapping && (
            <div className={clsx(styles.overlay, styles.error)}>
              <Tooltip
                message={intl.formatMessage({
                  id: 'reporting.map_datasets_or_remove',
                  defaultMessage: 'Map Datasets, or remove from Selected Datasets'
                })}
              >
                <span onClick={handleOnTriggerDSMapping}>
                  <Icon type="warning" fontSize="small" />
                </span>
              </Tooltip>
            </div>
          )}
          {!isFutrliTemplate && needsMapping && (
            <div className={clsx(styles.overlay, styles.error)}>
              <Tooltip message={tooltipText}>
                <span onClick={handleOnTriggerMapping}>
                  <Icon type="warning" fontSize="small" />
                </span>
              </Tooltip>
            </div>
          )}
          <div className={`${clsx(styles.overlay)} drag-handle`} style={{ cursor: 'grab', transform: 'rotate(45deg)' }}>
            <UIFeatherIcon iconName="move" svgAttributes={{ className: styles.moveIcon }} />
          </div>
          {onDelete && (
            <div className={clsx(styles.overlay)} onClick={handleOnDelete}>
              <Icon type="delete" fontSize="small" />
            </div>
          )}
          {onDuplicate && (
            <div className={clsx(styles.overlay)} onClick={handleOnDuplicate}>
              <Icon type="copy" fontSize="small" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
